// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Grid, Stack } from '@mui/material';
import { useLoadInitialValueFromDataset } from '../../../../hooks/DynamicValuesHooks';
import { useParameterConstraintValidation } from '../../../../hooks/ParameterConstraintsHooks';
import { BasicToggleInput, FadingTooltip } from '../../../../ui';
import { TranslationUtils, ScenarioParametersUtils } from '../../../../utils';

export const GenericToggleInput = ({
  parameterData,
  context,
  parameterValue,
  setParameterValue,
  resetParameterValue,
  isDirty,
  error,
}) => {
  const { t } = useTranslation();
  const switchFieldProps = {
    disabled: !context.editMode || ScenarioParametersUtils.isParameterReadOnly(parameterData),
    id: `toggle-input-${parameterData.id}`,
  };
  const { dynamicValue, loadingDynamicValuePlaceholder, dynamicValueErrorMessage } = useLoadInitialValueFromDataset(
    parameterValue,
    parameterData,
    context.targetDatasetId
  );

  useEffect(() => {
    if (parameterValue == null && dynamicValue != null && !isDirty && dynamicValue !== parameterValue) {
      if (parameterData.options?.dynamicValues) {
        resetParameterValue(dynamicValue);
      }
    }
  }, [parameterValue, dynamicValue, isDirty, resetParameterValue, parameterData.options?.dynamicValues]);

  if (loadingDynamicValuePlaceholder) return loadingDynamicValuePlaceholder;
  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Grid item xs={3}>
        <BasicToggleInput
          key={parameterData.id}
          id={parameterData.id}
          label={t(TranslationUtils.getParameterTranslationKey(parameterData.id), parameterData.id)}
          tooltipText={t(TranslationUtils.getParameterTooltipTranslationKey(parameterData.id), '')}
          value={parameterValue ?? false}
          changeSwitchType={setParameterValue}
          switchProps={switchFieldProps}
          isDirty={isDirty}
          error={error}
        />
      </Grid>
      {dynamicValueErrorMessage && (
        <FadingTooltip title={dynamicValueErrorMessage} placement={'right'}>
          <ErrorOutlineIcon data-cy="dynamic-value-error-icon" size="small" />
        </FadingTooltip>
      )}
    </Stack>
  );
};

GenericToggleInput.propTypes = {
  parameterData: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  parameterValue: PropTypes.any,
  setParameterValue: PropTypes.func.isRequired,
  resetParameterValue: PropTypes.func,
  isDirty: PropTypes.bool,
  error: PropTypes.object,
};
GenericToggleInput.defaultProps = {
  isDirty: false,
};
GenericToggleInput.useValidationRules = (parameterData) => {
  const { getParameterConstraintValidation } = useParameterConstraintValidation(parameterData);
  return {
    validate: {
      constraint: (v) => getParameterConstraintValidation(v),
    },
  };
};
